import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const FEATURES = [
  {
    icon: 'ri-lightbulb-line',
    title: '実務に基づくコンサルティング',
    desc: '現場での運用を前提とした、実行可能な施策をご提案いたします。理論だけでなく実際の業務に落とし込める改善案を提供します。',
  },
  {
    icon: 'ri-links-line',
    title: '一貫した支援体制',
    desc: '集客から営業までを一体的に支援し、売上向上に直結する仕組みを構築します。分断のない一気通貫のサポートが特徴です。',
  },
  {
    icon: 'ri-building-2-line',
    title: '中小企業に特化',
    desc: '限られたリソースの中でも成果を最大化する支援を得意としています。中小企業・個人事業主の現状に合った現実的な施策を提案します。',
  },
  {
    icon: 'ri-bar-chart-line',
    title: 'データドリブンな改善',
    desc: 'KPI設計・数値分析に基づき、感覚ではなくデータで判断する改善サイクルを構築。継続的な成果創出を実現します。',
  },
];

const HISTORY = [
  { year: '2017', label: '会社設立', desc: '株式会社Y・F・S設立。集客支援事業を開始。' },
  { year: '2018', label: '営業支援開始', desc: '営業支援コンサルティング事業を本格展開。' },
  { year: '2020', label: '支援企業100社突破', desc: '累計支援企業数が100社を超える。' },
  { year: '2022', label: 'デジタル強化', desc: 'デジタルマーケティング支援を強化。SNS・動画活用支援を追加。' },
  { year: '2024', label: '継続成長', desc: '継続率85%、支援企業150社超を達成。' },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-32 pb-16 bg-[#1a2332]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <span className="text-xs font-semibold tracking-[3px] text-white/40 uppercase">About</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">会社概要</h1>
          <p className="text-base text-white/60 max-w-xl">
            株式会社Y・F・Sは、中小企業の持続的成長を支える集客・営業支援コンサルティング会社です。
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold tracking-[3px] text-gray-400 uppercase">Our Mission</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mt-3 mb-6">
                安定した売上基盤の構築が、<br />企業成長の基本
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                企業の成長において、「安定した売上基盤の構築」は不可欠です。
                当社では、短期的な成果だけでなく、中長期的に持続可能な事業体制の構築を重視しております。
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                お客様の現状に寄り添いながら、実務に即した改善を積み重ねることで、
                着実な成長をご支援してまいります。
              </p>
              <Link
                to="/company-overview"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a2332] text-white text-sm font-medium rounded-lg hover:bg-[#2d3748] transition-colors whitespace-nowrap cursor-pointer"
              >
                会社概要詳細を見る <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://y-f-s.jp/img/gaiyou.jpeg"
                alt="会社のミッション"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-[#faf9f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold tracking-[3px] text-gray-400 uppercase">Our Strengths</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mt-3">当社の特徴</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-8 flex gap-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1a2332]/10 flex-shrink-0">
                  <i className={`${f.icon} text-xl text-[#1a2332]`} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1a2332] mb-2">{f.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Representative Message */}
      <section id="message" className="py-20 md:py-28 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-2">
              <div className="rounded-2xl overflow-hidden aspect-[3/4]">
                <img
                  src="https://y-f-s.jp/img/CEO.jpeg"
                  alt="代表取締役 林 裕次郎"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="lg:col-span-3">
              <span className="text-xs font-semibold tracking-[3px] text-gray-400 uppercase">Message</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mt-3 mb-8">代表メッセージ</h2>

              <i className="ri-double-quotes-l text-5xl text-[#1a2332]/10" />
              <div className="space-y-4 text-base text-gray-600 leading-relaxed mb-8">
                <p>
                  企業の成長において、「安定した売上基盤の構築」は不可欠です。
                  しかし、多くの中小企業では、集客の方法がわからなかったり、
                  営業活動が属人的になってしまい、安定した成果が出ないことに悩まれています。
                </p>
                <p>
                  当社では、短期的な成果だけでなく、中長期的に持続可能な事業体制の構築を重視しております。
                  お客様の現状に寄り添いながら、実務に即した改善を積み重ねることで、着実な成長をご支援してまいります。
                </p>
                <p>
                  「集客」と「営業」を一体的に強化することで、
                  単なる売上増加だけでなく、安定した収益基盤の構築を目指します。
                  皆様のビジネスの成長に貢献できることを、心より楽しみにしております。
                </p>
              </div>

              <div className="flex items-center gap-4 border-t border-gray-100 pt-6">
                <div>
                  <div className="text-sm text-gray-500">株式会社Y・F・S</div>
                  <div className="text-lg font-bold text-[#1a2332]">代表取締役　林 裕次郎</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-20 bg-[#faf9f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold tracking-[3px] text-gray-400 uppercase">History</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mt-3">沿革</h2>
          </div>
          <div className="max-w-2xl mx-auto">
            {HISTORY.map((item, i) => (
              <div key={item.year} className="flex gap-6 pb-8 relative">
                {i < HISTORY.length - 1 && (
                  <div className="absolute left-[27px] top-10 bottom-0 w-px bg-gray-200" />
                )}
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1a2332] text-white text-xs font-bold flex-shrink-0 z-10">
                  {item.year.slice(2)}
                </div>
                <div className="pt-3">
                  <div className="text-xs text-gray-400 mb-1">{item.year}年</div>
                  <div className="text-base font-bold text-[#1a2332] mb-1">{item.label}</div>
                  <div className="text-sm text-gray-600">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1a2332]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            まずはお気軽にご相談ください
          </h2>
          <p className="text-white/60 mb-8">
            無料相談を受け付けております。お気軽にお問い合わせください。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1a2332] font-semibold rounded-full hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer"
          >
            お問合せ <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
